import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
  alpha,
} from '@mui/material';
import type { SePayTransactionRow } from '../../services/api';

const PAGE_SIZE = 20;

const MONEY_FMT = new Intl.NumberFormat('vi-VN');

const statusChipProps = (
  s: string,
): { color: 'success' | 'primary' | 'error' | 'default'; variant: 'filled' | 'outlined' } => {
  if (s === 'CAPTURED') return { color: 'success', variant: 'filled' };
  if (s === 'PENDING') return { color: 'primary', variant: 'outlined' };
  if (s === 'CANCELLED' || s === 'FAILED') return { color: 'error', variant: 'outlined' };
  return { color: 'default', variant: 'outlined' };
};

const statusLabel = (s: string): string => {
  if (s === 'CAPTURED') return 'Đã thanh toán';
  if (s === 'PENDING') return 'Chờ thanh toán';
  return s;
};

export interface SePayTransactionsTableProps {
  rows: SePayTransactionRow[];
  loading?: boolean;
  /** user = cột Phương thức + paidAt; admin = Nội dung + createdAt */
  variant?: 'user' | 'admin';
  maxHeight?: number;
}

const SePayTransactionsTable: React.FC<SePayTransactionsTableProps> = ({
  rows,
  loading = false,
  variant = 'user',
  maxHeight = 360,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [rows]);

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);

  const pageRows = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return rows.slice(start, start + PAGE_SIZE);
  }, [rows, safePage]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (rows.length === 0) {
    return (
      <Box
        sx={{
          py: 3.5,
          px: 2,
          textAlign: 'center',
          borderRadius: 2.5,
          border: '1px dashed',
          borderColor: 'divider',
          bgcolor: (t) => alpha(t.palette.primary.main, 0.03),
        }}
      >
        <Typography variant="body2" color="text.secondary" fontWeight={600}>
          Chưa có giao dịch SePay.
        </Typography>
      </Box>
    );
  }

  const timeOf = (r: SePayTransactionRow) =>
    variant === 'user'
      ? r.paidAt
        ? new Date(r.paidAt).toLocaleString('vi-VN')
        : '—'
      : new Date(r.createdAt).toLocaleString('vi-VN');

  const pager = (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      alignItems="center"
      justifyContent="space-between"
      spacing={1}
    >
      <Typography variant="caption" color="text.secondary" sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
        Trang {safePage}/{totalPages} · {(safePage - 1) * PAGE_SIZE + 1}–
        {Math.min(safePage * PAGE_SIZE, rows.length)} / {rows.length}
      </Typography>
      <Pagination
        count={totalPages}
        page={safePage}
        onChange={(_, p) => setPage(p)}
        color="primary"
        shape="rounded"
        size={isMobile ? 'small' : 'medium'}
        showFirstButton={!isMobile}
        showLastButton={!isMobile}
        siblingCount={isMobile ? 0 : 1}
        boundaryCount={1}
      />
    </Stack>
  );

  if (isMobile) {
    return (
      <Stack spacing={1.25}>
        {pageRows.map((r) => {
          const chip = statusChipProps(r.status);
          return (
            <Box
              key={r._id}
              sx={{
                p: 1.5,
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'ui-monospace, monospace',
                      fontWeight: 700,
                      fontSize: '0.8rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {r.orderInvoiceNumber}
                  </Typography>
                  {variant === 'admin' && r.description ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: 'block',
                        mt: 0.25,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {r.description}
                    </Typography>
                  ) : null}
                  {variant === 'user' && r.paymentMethod ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                      {r.paymentMethod}
                    </Typography>
                  ) : null}
                </Box>
                <Chip
                  label={statusLabel(r.status)}
                  size="small"
                  color={chip.color}
                  variant={chip.variant}
                  sx={{ fontWeight: 700, fontSize: '0.68rem', height: 24, flexShrink: 0 }}
                />
              </Stack>
              <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mt: 1.1 }}>
                <Typography variant="subtitle2" fontWeight={800} color="primary.main">
                  {MONEY_FMT.format(r.amount)} đ
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {timeOf(r)}
                </Typography>
              </Stack>
            </Box>
          );
        })}
        {pager}
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <TableContainer
        sx={{
          maxHeight,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'auto',
        }}
      >
        <Table size="medium" stickyHeader sx={{ minWidth: 520 }}>
          <TableHead>
            <TableRow
              sx={{
                '& th': {
                  fontWeight: 700,
                  fontSize: '0.8125rem',
                  letterSpacing: '0.02em',
                  color: 'text.secondary',
                  bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(15, 23, 42, 0.04)'),
                  py: 1.75,
                },
              }}
            >
              <TableCell>Mã đơn</TableCell>
              <TableCell align="right">Số tiền</TableCell>
              {variant === 'admin' ? <TableCell>Nội dung</TableCell> : null}
              <TableCell>Trạng thái</TableCell>
              {variant === 'user' ? <TableCell>Phương thức</TableCell> : null}
              <TableCell>Thời gian</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pageRows.map((r) => {
              const chip = statusChipProps(r.status);
              const timeStr = timeOf(r);

              return (
                <TableRow
                  key={r._id}
                  hover
                  sx={{ '&:last-of-type td': { borderBottom: 0 }, '& td': { py: 1.5, borderColor: 'divider' } }}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'ui-monospace, monospace', fontWeight: 600 }}>
                      {r.orderInvoiceNumber}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={600}>
                      {MONEY_FMT.format(r.amount)} đ
                    </Typography>
                  </TableCell>
                  {variant === 'admin' ? (
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 200,
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        color={r.description ? 'text.primary' : 'text.disabled'}
                        title={r.description || undefined}
                      >
                        {r.description || '—'}
                      </Typography>
                    </TableCell>
                  ) : null}
                  <TableCell>
                    <Chip
                      label={statusLabel(r.status)}
                      size="small"
                      color={chip.color}
                      variant={chip.variant}
                      sx={{ fontWeight: 600 }}
                    />
                  </TableCell>
                  {variant === 'user' ? (
                    <TableCell>
                      <Typography variant="body2" color={r.paymentMethod ? 'text.primary' : 'text.disabled'}>
                        {r.paymentMethod || '—'}
                      </Typography>
                    </TableCell>
                  ) : null}
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ whiteSpace: 'nowrap' }}
                      color={timeStr === '—' ? 'text.disabled' : 'text.primary'}
                    >
                      {timeStr}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {pager}
    </Stack>
  );
};

export default SePayTransactionsTable;
